/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import template from './print-line-item-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {GetCampaignQrCodeModalComponent} from './get-campaign-qr-code-modal.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {buildElectionFormOptions, buildElectionSelectGroups, sameElectionId} from '../../components/util/election-form-options';
import {
  advertiserNeedsPoliticalData,
  buildTopicsFromPoliticalBudgets,
  linkedPoliticalCampaignRefs,
  loadPoliticalCampaignTopicsByElection
} from '../../components/util/political-campaign-advertiser-topics';
import {AdvertiserApiService} from '../advertiser/advertiser-api.service';
import {PublicPoliticalCampaignApiService} from '../political-campaign/public-political-campaign-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {compareById} from '../common/util/object-id-comparison';
import {PrintLineItemApiService, PrintLineItemModel} from './print-line-item-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {SearchSelectComponent} from '../common/controller/search-select.component';
import {FormControlsModule} from '../common/directives/form-controls/form-controls.module';
import {deferSignalStateWrite} from '../common/util/template-state';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, SearchSelectComponent, FormControlsModule],
  selector: 'adn-print-line-item-edit-page',
  template
})
export class PrintLineItemEditPageComponent extends SignalAdminResourceEditPageBase<PrintLineItemModel> {
  showAdvertiserSearch: boolean;
  elections: any[] = [];
  electionSelectGroups: any = {dated: [], undated: [], useGroups: false, groups: []};
  private readonly advertisersState = signal<any[]>([]);
  advertiserBudgets: any = null;
  advertiserBudgetsResolved = true;

  get advertisers(): any[] { return this.advertisersState(); }
  set advertisers(value: any[]) { this.advertisersState.set(value); }

  readonly selectObj = {
    onSelect: () => {
      if (!this.advertiserBudgets) {
        this.loadUpAdvertiser();
      }
    }
  };

  constructor(
    private readonly advertiserApi: AdvertiserApiService,
    private readonly publicPoliticalCampaignApi: PublicPoliticalCampaignApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly angularModalService: AngularModalService,
    private readonly printLineItemApi: PrintLineItemApiService,
    private readonly ngZone: NgZone,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(printLineItemApi, 'printLineItem');
    const advCount = this.localNetworkInfo.getInfo('advertiserCount');
    this.showAdvertiserSearch = !_.isFinite(advCount) || advCount >= 50;
  }

  get electionTopics(): string[] {
    const electionId = _.get(this.editingModel, ['election', 'id']);
    return (this.advertiserBudgets && electionId && this.advertiserBudgets[electionId]) || [];
  }

  readonly compareById = compareById;

  protected override afterEditingModelReset(): void {
    if (this.editingModel.startDate) {
      this.editingModel.modStartDate = this.editingModel.startDate;
    }
    if (this.editingModel.endDate) {
      this.editingModel.modEndDate = this.editingModel.endDate;
    }

    const selectedElectionId = _.get(this.editingModel, ['election', 'id']);
    this.elections = buildElectionFormOptions(this.localNetworkInfo.getSuppInfo('elections') || [], {
      username: this.localUserProfile.get('username'),
      canSeeTestElections: this.localUserProfile.canSeeTestElections(),
      selectedElectionId: selectedElectionId,
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);

    const selectedElection = _.find(this.elections, (election: any) => {
      return sameElectionId(election.id, selectedElectionId);
    });
    if (selectedElection) {
      this.editingModel.election = selectedElection;
    }

    if (advertiserNeedsPoliticalData(this.editingModel.advertiser)) {
      this.loadUpAdvertiser();
    } else {
      this.loadAdvertiserBudgets();
    }

    if (!this.showAdvertiserSearch && !this.advertisers.length) {
      this.advertiserApi.listOptions({minimal: true, pageSize: 1000}).subscribe((page: any) => {
        this.ngZone.run(() => {
          deferSignalStateWrite(() => this.advertisers = page.results);
        });
      });
    }
  }

  protected override prepareForSubmit(model: PrintLineItemModel): void {
    model.startDate = printLineItemDateForSubmit(model.modStartDate);
    model.endDate = printLineItemDateForSubmit(model.modEndDate);
  }

  onAdvertiserChange(): void {
    this.selectObj.onSelect();
    this.markExternalFormDirty();
  }

  getQrCode(): void {
    const openModal = (qrSuccess: boolean, image?: string, eps?: string, link?: string) => {
      this.angularModalService.show(GetCampaignQrCodeModalComponent, {
        class: 'modal-sm',
        initialState: {
          qrSuccess,
          image: image || '',
          eps: eps || '',
          link: link || ''
        }
      });
    };

    firstValueFrom(this.printLineItemApi.getLink(this.editingModel.id)).then(
      (response: any) => {
        openModal(true, response.qrCode, response.qrCodeEps, response.link);
      },
      () => {
        openModal(false);
      }
    );
  }

  private loadUpAdvertiser(): void {
    this.advertiserApi.get(_.get(this.editingModel, ['advertiser', 'id'])).subscribe((advertiser: any) => {
      this.ngZone.run(() => {
        this.editingModel.advertiser = advertiser;
        this.applyAdvertiserBudgets(advertiser);
      });
    });
  }

  private loadAdvertiserBudgets(): void {
    const advertiser = this.editingModel.advertiser;
    if (!advertiser) {
      this.advertiserBudgets = null;
      this.advertiserBudgetsResolved = true;
      return;
    }

    if (advertiserNeedsPoliticalData(advertiser)) {
      this.loadUpAdvertiser();
      return;
    }

    this.applyAdvertiserBudgets(advertiser);
  }

  private applyAdvertiserBudgets(advertiser: any): void {
    const linkedCampaigns = linkedPoliticalCampaignRefs(advertiser);
    if (linkedCampaigns.length > 0) {
      this.advertiserBudgetsResolved = false;
      loadPoliticalCampaignTopicsByElection(linkedCampaigns, this.publicPoliticalCampaignApi).then((campaignTopics: any) => {
        this.ngZone.run(() => {
          this.advertiserBudgets = campaignTopics;
          this.advertiserBudgetsResolved = true;
        });
      });
      return;
    }

    this.advertiserBudgets = buildTopicsFromPoliticalBudgets(advertiser);
    this.advertiserBudgetsResolved = true;
  }
}

export function printLineItemDateForSubmit(value: any): Date | null {
  if (!value) {
    return null;
  }
  const date = _.isDate(value) ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
